import { useEffect, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingSpotFeeAdjustedPriceQuerySchema } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { previewBiddingSpotBidFeeAdjustedPrices } from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import { PreviewBiddingSpotBidFeeAdjustedPricesRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import { skipToken } from '@tanstack/react-query'

import {
  type FeeAdjustedPricePreviewQueryStatus,
  type FeeAutoConsiderPreview,
  previewQueryStatus,
  revealsFeeUnavailable,
} from '~/models/feeAutoConsiderPreview'
import {
  resolveSpotFeeAutoConsiderPreview,
  spotFeeTreatment,
} from '~/pages/SpotBidding/models/feePreview'
import type { SpotFeeAutoConsider } from '~/pages/SpotBidding/models/spotFeeAutoConsider'

export type SpotFeeAdjustedPriceInput = {
  areaCode: AreaCode
  direction: Side
  desiredPriceYenPerKwh: string
}

export type SpotFeeAutoConsiderPreviewState = {
  /** 価格欄の解釈 (欄名・案内)。 開いた後にプレビューが手数料単価を使えないと示したら、 閉じるまで `fallbackOff`。 */
  fee: SpotFeeAutoConsider
  /** 作成してよいか・何を表示するか。 */
  preview: FeeAutoConsiderPreview
  /**
   * backend が入力を拒否した (指数表記・0.01 刻みでない希望価格等)。 手数料単価を使えないのではないので OFF に
   * 固定せず、 登録時に価格欄の項目エラーにする。
   */
  invalidInput: boolean
}

/** 価格を打ち終えるまで問い合わせを待つ時間。 */
const PREVIEW_DEBOUNCE_MS = 300

const keyOf = (input: SpotFeeAdjustedPriceInput | undefined): string =>
  input === undefined
    ? ''
    : `${input.areaCode}:${input.direction}:${input.desiredPriceYenPerKwh}`

export type SpotFeeAutoConsiderPreviewsState = {
  /** 価格欄の解釈 (欄名・案内)。 全行で 1 つ ([[useSpotFeeAutoConsiderPreview]] と同じ規則)。 */
  fee: SpotFeeAutoConsider
  /** 行ごとの判定 (`inputs` と同じ順・同じ長さ)。 */
  previews: FeeAutoConsiderPreview[]
  /** backend が入力を拒否した ([[SpotFeeAutoConsiderPreviewState]] の `invalidInput`)。 1 回の問い合わせ全体の値。 */
  invalidInput: boolean
}

/**
 * 個別作成モーダルの手数料自動考慮 (スポット) の入札価格を backend のプレビュー RPC で引く
 * (AC-BSP-004b / AC-BSP-106、 #8109)。 `fee` は開いているモーダルの扱い ([[useSpotFeeAutoConsider]]) で、
 * プレビューの状態を正として突き合わせる (食い違いは止める)。
 *
 * `input` が undefined の間 (入力が揃っていない・成行・下書きを開いた経路) と、 OFF として扱う間は
 * 問い合わせない。 入力が止まってから問い合わせ、 入力が最新の結果に追いつくまでは作成を止める。
 * 開いた後にプレビューが手数料単価を使えないと示したら、 閉じるまで OFF として扱い (入力値をそのまま
 * 入札価格にする。 途中で使えるようになっても戻さない)、 次に開いた時から元の扱いに戻す (AC-BSP-004b)。
 */
export function useSpotFeeAutoConsiderPreview({
  isOpen,
  input,
  fee,
}: {
  isOpen: boolean
  input: SpotFeeAdjustedPriceInput | undefined
  fee: SpotFeeAutoConsider
}): SpotFeeAutoConsiderPreviewState {
  const state = useSpotFeeAutoConsiderPreviews({ isOpen, inputs: [input], fee })
  const [preview] = state.previews
  if (preview === undefined) {
    throw new Error('useSpotFeeAutoConsiderPreviews returned no row')
  }
  return { fee: state.fee, preview, invalidInput: state.invalidInput }
}

/**
 * [[useSpotFeeAutoConsiderPreview]] の複数行版 (ブロック入札の Leg ごと・一括作成のエリアごと)。 行を 1 回の
 * 問い合わせに載せ、 OFF への切り替えは全行で 1 つにする (行ごとに分けると、 一部の行だけ OFF に固定されて
 * 欄名と扱いが行で食い違う)。 undefined の行は問い合わせに載せず `idle` で返す。
 */
export function useSpotFeeAutoConsiderPreviews({
  isOpen,
  inputs,
  fee: feeAutoConsider,
  enabled = true,
}: {
  isOpen: boolean
  inputs: ReadonlyArray<SpotFeeAdjustedPriceInput | undefined>
  fee: SpotFeeAutoConsider
  /** 入力のdebounceを保ちながら、逆算・欄への反映を待つ間のforward問い合わせを止める。 */
  enabled?: boolean
}): SpotFeeAutoConsiderPreviewsState {
  const [fallenBack, setFallenBack] = useState(false)
  if (!isOpen && fallenBack) setFallenBack(false)
  // 開いた描画には前に開いた時の入力への応答 (閉じても cache に残る) がまだ残っているので、
  // OFF へ切り替える引き金は開いた後の描画から受け取る。
  const [acceptsReport, setAcceptsReport] = useState(false)
  useEffect(() => {
    setAcceptsReport(isOpen)
  }, [isOpen])

  const fee: SpotFeeAutoConsider = fallenBack
    ? { kind: 'fallbackOff' }
    : feeAutoConsider
  const treatment = spotFeeTreatment(fee)

  const key = inputs.map(keyOf).join('|')
  const [settledKey, setSettledKey] = useState(key)
  useEffect(() => {
    const id = setTimeout(() => setSettledKey(key), PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [key])
  const rows = inputs.filter(
    (input): input is SpotFeeAdjustedPriceInput => input !== undefined,
  )
  const query =
    enabled &&
    rows.length > 0 &&
    settledKey === key &&
    fee.kind !== 'fallbackOff'
      ? rows
      : undefined

  const { data, isFetching, isError, failureCount, error } = useQuery(
    previewBiddingSpotBidFeeAdjustedPrices,
    query === undefined
      ? skipToken
      : create(PreviewBiddingSpotBidFeeAdjustedPricesRequestSchema, {
          queries: query.map((row) =>
            create(BiddingSpotFeeAdjustedPriceQuerySchema, {
              areaCode: row.areaCode,
              direction: row.direction,
              desiredPriceYenPerKwh: { value: row.desiredPriceYenPerKwh },
            }),
          ),
        }),
    { throwOnError: false },
  )
  // 入力の誤りを「手数料単価を使えない」と読むと閉じるまで OFF に固定し、 価格を直しても手数料を加減算しない
  // 価格を保存してしまう。 共通の判定 (時間前と共有) は変えず、 スポットの hook で問い合わせ前の状態に戻す。
  const invalidInput =
    query !== undefined &&
    error instanceof ConnectError &&
    error.code === Code.InvalidArgument
  const queryStatus: FeeAdjustedPricePreviewQueryStatus =
    query === undefined
      ? 'loading'
      : previewQueryStatus({
          waiting: isFetching,
          isError,
          failureCount,
          hasData: data !== undefined,
        })
  let rowIndex = 0
  const previews = inputs.map((input) => {
    const resultIndex = input === undefined ? undefined : rowIndex++
    return resolveSpotFeeAutoConsiderPreview({
      status: input === undefined || invalidInput ? 'idle' : queryStatus,
      state: data?.feeAutoConsider,
      result:
        resultIndex === undefined ? undefined : data?.results[resultIndex],
      side: input?.direction ?? Side.UNSPECIFIED,
      treatment,
    })
  })
  if (
    acceptsReport &&
    isOpen &&
    !fallenBack &&
    previews.some(revealsFeeUnavailable)
  ) {
    setFallenBack(true)
  }
  return { fee, previews, invalidInput }
}
